import React from 'react';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { routers } from 'router/router';
import { useAppDispatch } from 'app/hooks';
import { getCurrentBasicDetail } from 'app/basicInformationSlice/basicInformationSlice';
import { getAccessToken, getCurrentUserEmail, getUserId } from 'utils/sessionStorage';
import {
  CAM_NAVIGATE_REQUEST_EVENT,
  CamNavigateTarget,
  consumeCamNavigateRequest,
} from 'utils/camAuth';

/**
 * Performs HashRouter navigation for CAM upgrade modal actions.
 * CamUpgradeModalHost lives outside Router and cannot call useHistory directly.
 */
const CamOAuthNavigationBridge = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const navigate = React.useCallback(
    async (target: CamNavigateTarget) => {
      if (target === 'upgrade-plan') {
        // Start free trial: treat as a normal GradMap session so the billing
        // page has student context loaded (matches standard login flow).
        const studentId =
          sessionStorage.getItem('session_student_id') || getUserId();
        if (getAccessToken() && studentId) {
          // Payment checkout reads studentId/email from cookies (same as the
          // standard "no plan → payment" branch in FormLogin.processLoginSuccess).
          Cookies.set('request_id', String(studentId));
          const email = getCurrentUserEmail();
          if (email) {
            Cookies.set('userEmail', email);
          }
          try {
            await dispatch(getCurrentBasicDetail(studentId));
          } catch {
            /* non-blocking — billing page can still render */
          }
        }
        history.replace(routers.upgradePlan.path);
        return;
      }
      history.replace(routers.login.path);
    },
    [dispatch, history],
  );

  const runPending = React.useCallback(() => {
    const target = consumeCamNavigateRequest();
    if (target) {
      void navigate(target);
    }
  }, [navigate]);

  React.useLayoutEffect(() => {
    runPending();
    const onRequest = () => runPending();
    window.addEventListener(CAM_NAVIGATE_REQUEST_EVENT, onRequest);
    return () => window.removeEventListener(CAM_NAVIGATE_REQUEST_EVENT, onRequest);
  }, [runPending]);

  return null;
};

export default CamOAuthNavigationBridge;
